import { useState, useMemo } from 'react';
import Masonry from 'react-masonry-css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveVideo, buildSrcSet, getImageSizes } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function MediaGallerySection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const accent = config.accent_color || primaryColor;
    const [lbIndex, setLbIndex] = useState(-1);
    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);

    // Build items: resolve image URLs from mediaFolders for each item
    const rawItems = content.items || [];
    const items = rawItems.map(item => {
        if (item.type === 'video') {
            const vid = resolveVideo(item, 'video', mediaFolders);
            return { ...item, url: item.url, _resolvedVideo: vid };
        }
        // For images: resolve from folder or use stored image_url/url
        let url = item.image_url || item.url;
        let _file = null;
        if (item.folder_id && item.file_id && mediaFolders[item.folder_id]) {
            const found = mediaFolders[item.folder_id].find(f => String(f.id) === String(item.file_id));
            if (found) { url = found.image_url; _file = found; }
        }
        return { ...item, url, thumbnail_url: url, _file };
    }).filter(item => item.url);

    if (items.length === 0 && rawItems.length === 0) return null;
    if (items.length === 0) return (
        <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle }}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <SectionHeader tag={content.tag} title={content.title || 'Galeria multimedia'} subtitle={content.subtitle} config={config} s={s} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {rawItems.map((item, i) => (
                        <div key={i} className="aspect-square rounded-xl bg-gray-200 flex items-center justify-center">
                            <div className="text-center">
                                <svg className="w-8 h-8 text-gray-300 mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                <span className="text-[10px] text-gray-400">{item.title || 'Imagen'}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );

    const mgColumns = config.columns || 4;
    const mgColClass = mgColumns === 2 ? 'md:grid-cols-2'
        : mgColumns === 3 ? 'md:grid-cols-3'
        : mgColumns === 5 ? 'md:grid-cols-3 lg:grid-cols-5'
        : 'md:grid-cols-3 lg:grid-cols-4';

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;

    const getVideoId = (url) => {
        if (!url) return null;
        const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
        if (yt) return { type: 'youtube', id: yt[1] };
        const vim = url.match(/vimeo\.com\/(\d+)/);
        if (vim) return { type: 'vimeo', id: vim[1] };
        return null;
    };



    // ── Filterable variant ──
    if ((config.variant || 'grid') === 'filterable') {
        const [activeFilter, setActiveFilter] = useState('all');
        const categories = useMemo(() => {
            const cats = new Set();
            items.forEach(item => {
                const cat = item.category || item.tag;
                if (cat) cats.add(cat);
            });
            return Array.from(cats);
        }, [items]);
        const filteredItems = activeFilter === 'all' ? items : items.filter(item => (item.category || item.tag) === activeFilter);

        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    {categories.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-8">
                            <button onClick={() => setActiveFilter('all')}
                                className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300"
                                style={activeFilter === 'all'
                                    ? { backgroundColor: accent, color: '#fff' }
                                    : { backgroundColor: hexToRgba(accent, 0.08), color: s.text }}>
                                Todos
                            </button>
                            {categories.map(cat => (
                                <button key={cat} onClick={() => setActiveFilter(cat)}
                                    className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300"
                                    style={activeFilter === cat
                                        ? { backgroundColor: accent, color: '#fff' }
                                        : { backgroundColor: hexToRgba(accent, 0.08), color: s.text }}>
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className={`grid grid-cols-2 ${mgColClass} gap-4`}>
                        {filteredItems.slice(0, visibleCount).map((item, i) => {
                            const isVideo = item.type === 'video';
                            const thumb = item.thumbnail_url || (isVideo && getVideoId(item.url)?.type === 'youtube'
                                ? `https://img.youtube.com/vi/${getVideoId(item.url).id}/hqdefault.jpg`
                                : item.url);
                            return (
                                <div key={`${activeFilter}-${i}`}
                                    className="relative group cursor-pointer rounded-xl overflow-hidden aspect-square transition-all duration-500 hover:shadow-xl animate-[fadeIn_0.4s_ease-out]"
                                    style={{ animationDelay: `${Math.min(i, 9) * 50}ms`, animationFillMode: 'both' }}
                                    onClick={() => setLbIndex(items.indexOf(item))}>
                                    {thumb && <img src={thumb} alt={item.title || ''} srcSet={buildSrcSet(item._file)} sizes={getImageSizes(mgColumns)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" decoding="async"/>}
                                    {!thumb && (
                                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(accent, 0.08) }}>
                                            <svg className="w-12 h-12" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                            </svg>
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                                        {isVideo ? (
                                            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                                <svg className="w-6 h-6 ml-1" style={{ color: accent }} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                            </div>
                                        ) : (
                                            <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                                            </svg>
                                        )}
                                    </div>
                                    {item.title && (
                                        <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent">
                                            <p className="text-xs font-medium text-white truncate">{item.title}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {visibleCount < filteredItems.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, filteredItems.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({filteredItems.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>
                <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                    slides={lbIndex >= 0 ? items.map(img => ({ src: img.url, alt: img.title || '' })) : []} />
                <SectionDivider type={config.divider_bottom} color="transparent" config={config}/>
            </section>
        );
    }

    // ── Justified variant ──
    if ((config.variant || 'grid') === 'justified') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="flex flex-wrap" style={{ gap: '3px' }}>
                        {items.slice(0, visibleCount).map((item, i) => {
                            const isVideo = item.type === 'video';
                            const thumb = item.thumbnail_url || (isVideo && getVideoId(item.url)?.type === 'youtube'
                                ? `https://img.youtube.com/vi/${getVideoId(item.url).id}/hqdefault.jpg`
                                : item.url);
                            return (
                                <div key={i}
                                    className={`relative group cursor-pointer overflow-hidden transition-all duration-500 hover:opacity-90 ${visible ? 'opacity-100' : 'opacity-0'}`}
                                    style={{ flexGrow: 1, minWidth: '180px', maxHeight: '280px', transitionDelay: visible ? `${Math.min(i, 9) * 60}ms` : '0ms' }}
                                    onClick={() => setLbIndex(i)}>
                                    {thumb && <img src={thumb} alt={item.title || ''} srcSet={buildSrcSet(item._file)} sizes="(max-width:640px) 50vw, (max-width:1024px) 33vw, 25vw" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" style={{ maxHeight: '280px' }} loading="lazy" decoding="async"/>}
                                    {!thumb && (
                                        <div className="w-full flex items-center justify-center" style={{ height: '200px', backgroundColor: hexToRgba(accent, 0.08) }}>
                                            <svg className="w-12 h-12" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                            </svg>
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                                        {isVideo ? (
                                            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                                <svg className="w-6 h-6 ml-1" style={{ color: accent }} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                            </div>
                                        ) : (
                                            <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                                            </svg>
                                        )}
                                    </div>
                                    {item.title && (
                                        <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent">
                                            <p className="text-xs font-medium text-white truncate">{item.title}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {visibleCount < items.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, items.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({items.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                    slides={lbIndex >= 0 ? items.map(img => ({ src: img.url, alt: img.title || '' })) : []} />
                <SectionDivider type={config.divider_bottom} color="transparent" config={config}/>
            </section>
        );
    }

    // ── Masonry variant ──
    if ((config.variant || 'grid') === 'masonry') {
        const breakpointColumns = {
            default: mgColumns,
            1024: Math.min(mgColumns, 3),
            768: 2,
            480: 1,
        };

        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <Masonry
                        breakpointCols={breakpointColumns}
                        className="flex -ml-4 w-auto"
                        columnClassName="pl-4 bg-clip-padding"
                    >
                        {items.slice(0, visibleCount).map((item, i) => {
                            const isVideo = item.type === 'video';
                            const thumb = item.thumbnail_url || (isVideo && getVideoId(item.url)?.type === 'youtube'
                                ? `https://img.youtube.com/vi/${getVideoId(item.url).id}/hqdefault.jpg`
                                : item.url);

                            return (
                                <div key={i}
                                    className={`mb-4 relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-500 hover:shadow-xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${Math.min(i, 9) * 80}ms` : '0ms' }}
                                    onClick={() => setLbIndex(i)}>
                                    {thumb && <img src={thumb} alt={item.title || ''} srcSet={buildSrcSet(item._file)} sizes="(max-width:640px) 100vw, (max-width:1024px) 50vw, 33vw" className="w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async"/>}
                                    {!thumb && (
                                        <div className="w-full aspect-square flex items-center justify-center" style={{ backgroundColor: hexToRgba(accent, 0.08) }}>
                                            <svg className="w-12 h-12" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                            </svg>
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                                        {isVideo ? (
                                            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                                <svg className="w-6 h-6 ml-1" style={{ color: accent }} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                            </div>
                                        ) : (
                                            <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                                            </svg>
                                        )}
                                    </div>
                                    {item.title && (
                                        <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent">
                                            <p className="text-xs font-medium text-white truncate">{item.title}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </Masonry>
                    {visibleCount < items.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, items.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({items.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>

                <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                    slides={lbIndex >= 0 ? items.map(img => ({ src: img.url, alt: img.title || '' })) : []} />
                <SectionDivider type={config.divider_bottom} color="transparent" config={config}/>
            </section>
        );
    }

    // ── Default grid variant ──
    return (
        <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}

                <div className={`grid grid-cols-2 ${mgColClass} gap-4`}>
                    {items.slice(0, visibleCount).map((item, i) => {
                        const isVideo = item.type === 'video';
                        const thumb = item.thumbnail_url || (isVideo && getVideoId(item.url)?.type === 'youtube'
                            ? `https://img.youtube.com/vi/${getVideoId(item.url).id}/hqdefault.jpg`
                            : item.url);

                        return (
                            <div key={i}
                                className={`relative group cursor-pointer rounded-xl overflow-hidden aspect-square transition-all duration-500 hover:shadow-xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 9) * 80}ms` : '0ms' }}
                                onClick={() => setLbIndex(i)}>
                                {thumb && <img src={thumb} alt={item.title || ''} srcSet={buildSrcSet(item._file)} sizes={getImageSizes(mgColumns)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" decoding="async"/>}
                                {!thumb && (
                                    <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(accent, 0.08) }}>
                                        <svg className="w-12 h-12" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                        </svg>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                                    {isVideo ? (
                                        <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                            <svg className="w-6 h-6 ml-1" style={{ color: accent }} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                        </div>
                                    ) : (
                                        <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                                        </svg>
                                    )}
                                </div>
                                {item.title && (
                                    <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent">
                                        <p className="text-xs font-medium text-white truncate">{item.title}</p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {visibleCount < items.length && (
                    <div className="text-center mt-8">
                        <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, items.length))}
                            className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                            style={{borderColor: s.accent, color: s.accent}}>
                            Ver mas ({items.length - visibleCount} restantes)
                        </button>
                    </div>
                )}
            </div>

            <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                slides={lbIndex >= 0 ? items.map(img => ({ src: img.url, alt: img.title || '' })) : []} />
            <SectionDivider type={config.divider_bottom} color="transparent" config={config}/>
        </section>
    );
}
